import { Button, ButtonProps, Icon } from "@mui/material";
import { useTranslation } from "react-i18next";

const minWidth = "125px";

export const AddButton: React.FC<ButtonProps> = ({ children, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button startIcon={<Icon>add_circle</Icon>} sx={{ minWidth: minWidth }} variant="contained" {...props}>
			{children || t("actions.add")}
		</Button>
	);
};

export const CancelButton: React.FC<ButtonProps> = ({ children, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button color="plain" sx={{ minWidth: "50px", width: "100px" }} variant="outlined" {...props}>
			{children || t("actions.cancel")}
		</Button>
	);
};

export const BackButton: React.FC<ButtonProps> = ({ children, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button startIcon={<Icon>arrow_back</Icon>} sx={{ minWidth: minWidth }} variant="outlined" {...props}>
			{children || t("actions.back")}
		</Button>
	);
};

export const ContinueButton: React.FC<ButtonProps> = ({ children, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button endIcon={<Icon>arrow_forward</Icon>} sx={{ minWidth: minWidth }} variant="contained" {...props}>
			{children || t("actions.continue")}
		</Button>
	);
};

export const WarningButton: React.FC<ButtonProps> = ({ children, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button color="warning" startIcon={<Icon>warning</Icon>} sx={{ minWidth: minWidth }} variant="contained" {...props}>
			{children || t("actions.proceedWithWarnings")}
		</Button>
	);
};

export const DeleteButton: React.FC<ButtonProps> = ({ children, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button color="error" startIcon={<Icon>delete</Icon>} sx={{ minWidth: "100px" }} {...props}>
			{children || t("actions.delete")}
		</Button>
	);
};

export const SubmitButton: React.FC<ButtonProps> = ({ children, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button color="primary" fullWidth sx={{ minWidth: minWidth }} variant="contained" {...props}>
			{children || t("actions.submit")}
		</Button>
	);
};

export const SaveButton: React.FC<ButtonProps> = ({ children, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button sx={{ minWidth: minWidth, width: "130px" }} variant="contained" {...props}>
			{children || t("actions.save")}
		</Button>
	);
};

export const CopyButton: React.FC<ButtonProps> = ({ children, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button
			color="success"
			startIcon={<Icon>content_copy</Icon>}
			sx={{ minWidth: minWidth }}
			variant="contained"
			{...props}
		>
			{children || t("actions.copy")}
		</Button>
	);
};

export const ZeroOutButton: React.FC<ButtonProps> = ({ children, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button
			color="accentOrange"
			startIcon={<Icon>replay</Icon>}
			sx={{ minWidth: minWidth }}
			variant="contained"
			{...props}
		>
			{children || t("actions.zeroOut")}
		</Button>
	);
};

export const LoadButton: React.FC<ButtonProps> = ({ children, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button
			color="success"
			startIcon={<Icon>folder_open</Icon>}
			sx={{ minWidth: minWidth }}
			variant="contained"
			{...props}
		>
			{children || t("actions.load")}
		</Button>
	);
};

interface ExportButtonProps extends ButtonProps {
	component?: React.ElementType;
	download?: boolean;
	href?: string;
	rel?: string;
	target?: string;
}

export const ExportButton: React.FC<ExportButtonProps> = ({ children, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button
			color="accentBlack"
			startIcon={<Icon>file_download</Icon>}
			sx={{ minWidth: minWidth }}
			variant="contained"
			{...props}
		>
			{children || t("actions.export")}
		</Button>
	);
};

export const CreateButton: React.FC<ButtonProps> = ({ children, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button
			color="accentGreen"
			startIcon={<Icon>add_circle</Icon>}
			sx={{ minWidth: minWidth }}
			variant="contained"
			{...props}
		>
			{children || t("actions.create")}
		</Button>
	);
};

export const EditButton: React.FC<ButtonProps> = ({ children, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button
			color="accentIndigo"
			startIcon={<Icon>edit</Icon>}
			sx={{ minWidth: minWidth }}
			variant="contained"
			{...props}
		>
			{children || t("actions.edit")}
		</Button>
	);
};

export const RetryButton: React.FC<ButtonProps> = ({ children, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button color="plain" startIcon={<Icon>refresh</Icon>} sx={{ minWidth: minWidth }} variant="outlined" {...props}>
			{children || t("actions.retry")}
		</Button>
	);
};

export const RefreshButton: React.FC<ButtonProps> = ({ children, sx, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button
			startIcon={<Icon>refresh</Icon>}
			sx={[{ minWidth: minWidth }, ...(Array.isArray(sx) ? sx : [sx])]}
			variant="outlined"
			{...props}
		>
			{children || t("actions.refresh")}
		</Button>
	);
};

export const ClearButton: React.FC<ButtonProps> = ({ children, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button color="plain" variant="text" {...props}>
			{children || t("actions.clear")}
		</Button>
	);
};

export const RemoveButton: React.FC<ButtonProps> = ({ children, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button color="error" startIcon={<Icon>delete</Icon>} variant="text" {...props}>
			{children || t("actions.remove")}
		</Button>
	);
};

export const ViewAllButton: React.FC<ButtonProps> = ({ children, sx, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button
			endIcon={<Icon sx={{ fontSize: "1rem" }}>arrow_forward</Icon>}
			size="small"
			sx={[{ fontWeight: 600, textTransform: "none" }, ...(Array.isArray(sx) ? sx : [sx])]}
			{...props}
		>
			{children || t("actions.viewAll")}
		</Button>
	);
};

export const DiscardButton: React.FC<ButtonProps> = ({ children, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button color="plain" variant="outlined" {...props}>
			{children || t("actions.discard")}
		</Button>
	);
};

export const OutlinedStatusButton: React.FC<ButtonProps> = ({ children, ...props }) => {
	return (
		<Button color="plain" sx={{ minWidth: minWidth }} variant="outlined" {...props}>
			{children}
		</Button>
	);
};

interface DescriptiveButtonProps extends ButtonProps {
	subtitle?: string;
}

export const DescriptiveButton: React.FC<DescriptiveButtonProps> = ({ children, startIcon, subtitle, ...props }) => {
	const { t } = useTranslation("common");
	return (
		<Button
			fullWidth
			size="large"
			sx={{
				"&:hover": { backgroundColor: "success.dark" },
				backgroundColor: "success.main",
				color: "white",
				flexDirection: "column",
				py: subtitle ? 1.5 : 2
			}}
			variant="contained"
			{...props}
		>
			<span
				style={{
					alignItems: "center",
					display: "flex",
					fontSize: "1.1rem",
					fontWeight: 600,
					gap: "8px"
				}}
			>
				{startIcon}
				<span>{children || t("actions.select")}</span>
			</span>
			{subtitle && (
				<span
					style={{
						fontSize: "0.875rem",
						fontWeight: 500,
						textTransform: "none"
					}}
				>
					{subtitle}
				</span>
			)}
		</Button>
	);
};
